<script setup lang="ts">
import { computed } from 'vue'
import { INVESTMENT_TYPE_LABELS, INVESTMENT_SIZE_LABELS } from '@/modules/connecting/constants/investment'
import OnboardingReviewItem from '@/modules/shared/components/onboarding/OnboardingReviewItem.vue'

const props = defineProps<{
  individual: any
  candidateData: {
    investment_types_interested: string[]
    fund_investment_range: string | null
    spv_investment_range: string | null
    deal_investment_range: string | null
    industries_interested: string[]
    university_affiliations: string[]
  }
  accreditationLabel: string
}>()

const emit = defineEmits<{
  (e: 'navigate', stepIndex: number): void
}>()

const name = computed(() => {
  const fn = props.individual?.first_name || ''
  const ln = props.individual?.last_name || ''
  return [fn, ln].filter(Boolean).join(' ') || '—'
})

const investVia = computed(() => {
  if (!props.candidateData.investment_types_interested.length) return '—'
  return props.candidateData.investment_types_interested
    .map((t) => {
      const label = INVESTMENT_TYPE_LABELS[t] || t
      let range = ''
      if (t === 'fund' && props.candidateData.fund_investment_range)
        range =
          INVESTMENT_SIZE_LABELS[props.candidateData.fund_investment_range] || props.candidateData.fund_investment_range
      if (t === 'syndicate' && props.candidateData.spv_investment_range)
        range =
          INVESTMENT_SIZE_LABELS[props.candidateData.spv_investment_range] || props.candidateData.spv_investment_range
      if (t === 'angel_group' && props.candidateData.deal_investment_range)
        range =
          INVESTMENT_SIZE_LABELS[props.candidateData.deal_investment_range] || props.candidateData.deal_investment_range
      return range ? `${label} (${range})` : label
    })
    .join(', ')
})

const interests = computed(
  () => props.candidateData.industries_interested.filter((i) => i !== 'Other...').join(', ') || '—',
)
const university = computed(() => props.candidateData.university_affiliations.join(', ') || 'None')

interface ReviewRow {
  label: string
  value: string
  stepIndex: number
}

const rows = computed<ReviewRow[]>(() => [
  { label: 'Name', value: name.value, stepIndex: 0 },
  { label: 'Date of birth', value: props.individual?.birthdate || '—', stepIndex: 0 },
  { label: 'Country', value: props.individual?.address?.country || props.individual?.country || '—', stepIndex: 0 },
  { label: 'LinkedIn', value: props.individual?.linkedin_url || '—', stepIndex: 0 },
  { label: 'Accreditation', value: props.accreditationLabel || '—', stepIndex: 1 },
  { label: 'Invest via', value: investVia.value, stepIndex: 2 },
  { label: 'Interests', value: interests.value, stepIndex: 3 },
  { label: 'University', value: university.value, stepIndex: 4 },
])
</script>

<template>
  <div>
    <OnboardingReviewItem
      v-for="row in rows"
      :key="row.label"
      :label="row.label"
      :value="row.value"
      @click="emit('navigate', row.stepIndex)"
    />
  </div>
</template>
